import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, CheckCircle2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { fmtXof } from './recMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = [
  { value: 'virement', label: 'Virement bancaire' },
  { value: 'mobile_money', label: 'Mobile Money (Wave / Orange / MTN)' },
  { value: 'cash', label: 'Espèces' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'card', label: 'Carte bancaire' },
  { value: 'autre', label: 'Autre' },
];

/**
 * Modal d'enregistrement de paiement (partiel ou total).
 * - invoiceType 'operation'  → POST /api/financial/{id}/record-payment
 * - invoiceType 'contract'   → POST /api/contract-invoices/{id}/payment
 */
export const RecordPaymentModal = ({ open, onClose, invoice, onSuccess }) => {
  const remaining = Number(invoice?.amountDue || 0);
  const alreadyPaid = Number(invoice?.amountPaid || 0);
  const totalAmount = Number(invoice?.amount || 0);

  const [amount, setAmount] = useState(remaining);
  const [method, setMethod] = useState('virement');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Reset à chaque ouverture pour éviter d'hériter d'un ancien montant.
    if (open) {
      setAmount(remaining);
      setMethod('virement');
      setReference('');
    }
  }, [open, remaining]);

  const parsedAmount = useMemo(() => Number(amount) || 0, [amount]);
  const isFinal = parsedAmount >= remaining && remaining > 0;
  const willBePartial = parsedAmount > 0 && parsedAmount < remaining;
  const invalidAmount = parsedAmount <= 0 || parsedAmount > remaining + 1;

  const submit = async () => {
    if (invalidAmount) {
      toast.error(`Le montant doit être compris entre 1 et ${fmtXof(remaining)}`);
      return;
    }
    setBusy(true);
    try {
      const target = invoice.markPaidTarget;
      if (target.type === 'operation') {
        await axios.post(`${API}/financial/${target.id}/record-payment`, {
          amount: parsedAmount,
          payment_method: method,
          payment_reference: reference || null,
          is_final: isFinal,
        });
      } else {
        // ContractInvoice PaymentMethod enum : virement | especes | cheque | mobile_money
        const contractMethodMap = {
          cash: 'especes', card: 'virement', autre: 'virement',
        };
        const contractMethod = contractMethodMap[method] || method;
        await axios.post(`${API}/contract-invoices/${target.id}/payment`, {
          amount: parsedAmount,
          method: contractMethod,
          reference: reference || null,
        });
      }
      toast.success(
        isFinal
          ? `Paiement final enregistré (${fmtXof(parsedAmount)}) — Facture soldée`
          : `Paiement partiel enregistré (${fmtXof(parsedAmount)})`
      );
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement du paiement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="record-payment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Enregistrer un paiement
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Saisissez le montant reçu (partiel ou total) et la méthode de paiement.
          </DialogDescription>
        </DialogHeader>

        {/* Résumé facture */}
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-xs">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-gray-500">Facture</span>
            <span className="font-mono font-semibold text-gray-800">{invoice?.invoiceRef}</span>
          </div>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-gray-500">Client</span>
            <span className="font-medium text-gray-800">{invoice?.clientName}</span>
          </div>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-gray-500">Montant total</span>
            <span className="font-semibold text-gray-800">{fmtXof(totalAmount)}</span>
          </div>
          {alreadyPaid > 0 && (
            <div className="mb-1 flex items-center justify-between">
              <span className="text-gray-500">Déjà encaissé</span>
              <span className="font-semibold text-emerald-700">{fmtXof(alreadyPaid)}</span>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-gray-200 pt-1 mt-1">
            <span className="text-gray-500">Restant dû</span>
            <span className="font-bold text-gray-900">{fmtXof(remaining)}</span>
          </div>
        </div>

        {/* Montant reçu */}
        <div className="space-y-1.5">
          <Label htmlFor="pay-amount" className="text-xs font-medium text-gray-700">
            Montant reçu (XOF) *
          </Label>
          <Input
            id="pay-amount"
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="text-right font-mono font-semibold"
            data-testid="record-payment-amount"
          />
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button" size="sm" variant="outline" className="h-6 px-2 text-[10px]"
              onClick={() => setAmount(Math.round(remaining / 2))}
              data-testid="record-payment-half"
            >
              50 % ({fmtXof(Math.round(remaining / 2))})
            </Button>
            <Button
              type="button" size="sm" variant="outline" className="h-6 px-2 text-[10px]"
              onClick={() => setAmount(remaining)}
              data-testid="record-payment-full"
            >
              Solder ({fmtXof(remaining)})
            </Button>
          </div>
          {willBePartial && (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 px-2.5 py-1.5 text-[11px] text-yellow-800">
              Paiement partiel — Reste dû après : {fmtXof(remaining - parsedAmount)}
            </div>
          )}
          {isFinal && parsedAmount > 0 && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] text-emerald-800">
              Paiement final — la facture sera marquée <b>Payée</b>
            </div>
          )}
        </div>

        {/* Méthode */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-gray-700">Méthode de paiement *</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="h-9" data-testid="record-payment-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_METHODS.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Référence */}
        <div className="space-y-1.5">
          <Label htmlFor="pay-ref" className="text-xs font-medium text-gray-700">
            Référence (optionnel)
          </Label>
          <Input
            id="pay-ref"
            placeholder="N° virement, ID transaction, N° chèque…"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            data-testid="record-payment-reference"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button" variant="outline" onClick={onClose} disabled={busy}
            data-testid="record-payment-cancel"
          >
            <X className="mr-1 h-4 w-4" /> Annuler
          </Button>
          <Button
            type="button"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={submit}
            disabled={busy || invalidAmount}
            data-testid="record-payment-submit"
          >
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}
            Enregistrer le paiement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecordPaymentModal;
